import React, { useEffect, useContext, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Redirect } from 'react-router-dom';

import { getActiveSite, setCreatedSite } from '../../bundles/sites';
import { getLayout } from '../../bundles/layouts';
import { getComponentsLoaded } from '../../bundles/components';
import { getVisualizationsLoaded } from '../../bundles/visualizations';
import { requestSources, getSourcesLoaded, getSiteVariablesUpdatedAt } from '../../bundles/sources';
import {
  setViewerReady,
  startSitePolling,
  stopSitePolling,
  initializeSitePolling,
  getTimezone,
} from '../../bundles/application';
import { getModelsLoaded } from '../../bundles/models';
import { getStateSetsLoaded } from '../../bundles/statesets';
import { requestIntegrations, getIntegrationsLoaded } from '../../bundles/integrations';
import { getOnline, displayNotification, getNotification } from '../../bundles/notifications';
import { getIsContributorOrAdmin } from '../../bundles/auth';
import AppContext from '../../AppContext';
import { datetimeString } from '../../datetimeUtils';

import Page from '../Page';
import { requestNotifyEvents, getEventNotifications } from '../../bundles/events';

const SiteDetailView = ({
  match: {
    params: { id: siteId, page },
  },
}) => {
  const dispatch = useDispatch();

  const layout = useSelector((state) => getLayout(state, siteId));
  const site = useSelector(getActiveSite);
  const sourcesLoaded = useSelector(getSourcesLoaded);
  const siteVariablesUpdatedAt = useSelector(getSiteVariablesUpdatedAt);
  const modelsLoaded = useSelector(getModelsLoaded);
  const componentsLoaded = useSelector(getComponentsLoaded);
  const visualizationsLoaded = useSelector(getVisualizationsLoaded);
  const integrationsLoaded = useSelector(getIntegrationsLoaded);
  const statesetsLoaded = useSelector(getStateSetsLoaded);
  const online = useSelector(getOnline);
  const timezone = useSelector(getTimezone);
  const eventNotifications = useSelector(getEventNotifications);
  const isContributorOrAdmin = useSelector((state) => getIsContributorOrAdmin(state, site));

  const { setIsFullScreen } = useContext(AppContext);
  const [loadingContributor, setLoadingContributor] = useState(true);

  useEffect(() => {
    dispatch(setCreatedSite(null));
    return () => {
      setIsFullScreen(false);
    };
  }, []);

  useEffect(() => {
    if (siteId) {
      dispatch(initializeSitePolling(siteId));
    }
  }, [siteId]);

  useEffect(() => {
    if (online) {
      dispatch(setViewerReady(false));
    }
  }, [online]);

  // We don't request sources in Router because Admin routes don't need to include withVariables
  useEffect(() => {
    if (online && siteId && !siteVariablesUpdatedAt) {
      dispatch(requestSources({ siteId, withVariables: true }));
    }
  }, [siteId, online, siteVariablesUpdatedAt]);

  useEffect(() => {
    if (online && siteId) {
      dispatch(requestIntegrations({ site: siteId }));
    }
  }, [siteId, online]);

  useEffect(() => {
    if (online && siteId) {
      dispatch(requestNotifyEvents(siteId));
    }
  }, [siteId, online]);

  useEffect(() => {
    if (eventNotifications.todoCount > 0 || eventNotifications.overdueCount > 0) {
      const timeStamp = datetimeString(Date.now(), timezone);

      dispatch(
        displayNotification(
          getNotification(
            'eventState',
            'eventNotification'
          )({
            todoCount: eventNotifications.todoCount,
            overdueCount: eventNotifications.overdueCount,
            timeStamp,
          })
        )
      );
    }
  }, [eventNotifications]);

  const assetsLoaded =
    sourcesLoaded &&
    modelsLoaded &&
    componentsLoaded &&
    visualizationsLoaded &&
    statesetsLoaded &&
    integrationsLoaded;

  useEffect(() => {
    if (assetsLoaded) {
      dispatch(startSitePolling());
      setLoadingContributor(false);
    }
    return () => {
      dispatch(stopSitePolling());
    };
  }, [assetsLoaded]);

  const { pages } = layout || { pages: [], type: 'pt' };

  if (!page) {
    if (loadingContributor) {
      return null;
    }
    const { isPublic, path } = pages?.[0] || {};
    if (isContributorOrAdmin && path && !isPublic) {
      return <Redirect to={`/sites/${siteId}/${pages[0].path}`} />;
      // eslint-disable-next-line no-else-return
    } else {
      const publicPageIndex = pages.findIndex((currentPage) => currentPage.isPublic);
      if (publicPageIndex !== -1) {
        return <Redirect to={`/sites/${siteId}/${pages[publicPageIndex].path}`} />;
        // eslint-disable-next-line no-else-return
      } else {
        return (
          <Redirect
            exact
            to="/sites/"
          />
        );
      }
    }
  }

  const pageConfig = pages && pages.find(({ path }) => path.toLowerCase() === page);

  return (
    <>
      <Page
        pages={pages}
        site={site}
        page={page}
        pageConfig={pageConfig}
      />
    </>
  );
};
export default SiteDetailView;
